"use client";

import React from "react";
import { Caveat } from "next/font/google";
import { motion } from "framer-motion";
import Image from "next/image";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

const avatarUrls = [
  "/odoo/unsplash/avatars/u1.jpg",
  "/odoo/unsplash/avatars/u2.jpg",
  "/odoo/unsplash/avatars/u3.jpg",
  "/odoo/unsplash/avatars/u4.jpg",
  "/odoo/unsplash/avatars/u5.jpg",
  "/odoo/unsplash/avatars/u6.jpg",
  "/odoo/unsplash/avatars/u7.jpg",
  "/odoo/unsplash/avatars/u8.jpg",
  "/odoo/unsplash/avatars/u9.jpg",
  "/odoo/unsplash/avatars/u10.jpg",
  "/odoo/unsplash/avatars/u11.jpg",
  "/odoo/unsplash/avatars/u12.jpg",
  "/odoo/unsplash/avatars/u13.jpg",
  "/odoo/unsplash/avatars/u14.jpg",
];

const TOTAL_BOXES = 60;

export default function IndustryUsersMosaic() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.8, y: 20 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  const pillVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 30 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 0.7, ease: "easeOut", delay: 0.4 },
    },
  };

  return (
    <section className="s_wd_users bg-white/5/10/80 px-6 py-14 sm:px-12 lg:px-24">
      <div className="container relative mx-auto min-h-[20rem]">
        <motion.div
          className="grid grid-cols-6 gap-4 sm:grid-cols-8 lg:grid-cols-12"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {Array.from({ length: TOTAL_BOXES }).map((_, idx) => {
            const showAvatar = idx % 7 === 1 || idx % 11 === 3;
            const tileTone = idx % 5;
            const toneClass =
              tileTone === 0
                ? "bg-[#d4d7dd]"
                : tileTone === 1
                  ? "bg-[#f1833b]"
                  : tileTone === 2
                    ? "bg-[#dddfe5]"
                    : tileTone === 3
                      ? "bg-[#d0d3db]"
                      : "bg-[#f1833b]";

            const shapeClass =
              idx % 6 === 0
                ? "rounded-[28px]"
                : idx % 6 === 1
                  ? "rounded-l-[30px] rounded-br-[30px]"
                  : idx % 6 === 2
                    ? "rounded-r-[30px] rounded-bl-[30px]"
                    : idx % 6 === 3
                      ? "rounded-full"
                      : idx % 6 === 4
                        ? "rounded-[12px]"
                        : "rounded-tr-[28px] rounded-bl-[28px]";

            return (
              <motion.div
                key={idx}
                variants={itemVariants}
                className={`h-20 w-full overflow-hidden relative ${shapeClass} ${showAvatar ? "bg-transparent" : toneClass}`}
              >
                {showAvatar && (
                  <Image
                    src={avatarUrls[idx % avatarUrls.length]}
                    alt=""
                    fill
                    sizes="80px"
                    className="object-cover"
                  />
                )}
              </motion.div>
            );
          })}
        </motion.div>

        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
          <motion.div
            className="rounded-[2.5rem] bg-black/55 backdrop-blur-md px-8 py-7 text-center shadow-[0_16px_45px_rgba(0,0,0,0.45)] sm:px-12"
            variants={pillVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            <div className="relative">
              <div className="flex items-center justify-end gap-2 mb-2">
                <Image
                  src="/odoo/graphics/arrow_circular_down_left.svg"
                  width={45}
                  height={45}
                  alt=""
                  className="hidden sm:block opacity-90 object-contain"
                />
                <span className={`text-3xl md:text-4xl sm:text-3xl text-[#f4a62d] ${caveat.className}`}>happy</span>
              </div>
              <h6 className={`mb-3 text-3xl sm:text-5xl md:text-6xl leading-snug text-white ${caveat.className}`}>
                Join 15 million users
              </h6>
            </div>
            <span className="text-xl text-gray-200 sm:text-2xl">who grow their business with Odoo</span>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
